import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth is handled by Cloudflare Access — the browser carries the Access
// cookie automatically and the API verifies the Access JWT. No auto-reload
// on 401: a persistent 401 would loop the page; AuthContext surfaces the
// failure state instead.

// Auth API
export const authAPI = {
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Admin API
export const adminAPI = {
  listUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
};

// Organizations API
export const organizationsAPI = {
  list: (params) => api.get('/organizations', { params }),
  get: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
  getEvents: (id) => api.get(`/organizations/${id}/events`),
};

// Contacts API
export const contactsAPI = {
  list: (params) => api.get('/contacts', { params }),
  get: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  getEvents: (id) => api.get(`/contacts/${id}/events`),
  getEventCounts: () => api.get('/contacts/event-counts'),
};

// Locations API (formerly Venues)
export const locationsAPI = {
  list: (params) => api.get('/locations', { params }),
  get: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  getEvents: (id) => api.get(`/locations/${id}/events`),
  listFiles: (id) => api.get(`/locations/${id}/files`),
  uploadFile: (id, formData) => api.post(`/locations/${id}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteFile: (fileId) => api.delete(`/files/${fileId}`),
  updateFileDescription: (fileId, description) => api.put(`/files/${fileId}`, { description }),
  downloadFile: (fileId) => api.get(`/files/${fileId}/download`, { responseType: 'blob' }),
};

// Legacy alias for backwards compatibility
export const venuesAPI = locationsAPI;

// Events API
export const eventsAPI = {
  list: (params) => api.get('/events', { params }),
  getPageForDate: (date, limit) => api.get('/events/page-for-date', { params: { date, limit } }),
  getFilterOptions: () => api.get('/events/filter-options'),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  copy: (id, data) => api.post(`/events/${id}/copy`, data),
  getContacts: (id) => api.get(`/events/${id}/contacts`),
  // Call Sheets
  getCallSheets: (eventId) => api.get(`/events/${eventId}/call-sheets`),
  createCallSheet: (eventId, data) => api.post(`/events/${eventId}/call-sheets`, data),
  updateCallSheet: (eventId, callSheetId, data) => api.put(`/events/${eventId}/call-sheets/${callSheetId}`, data),
  deleteCallSheet: (eventId, callSheetId) => api.delete(`/events/${eventId}/call-sheets/${callSheetId}`),
  generateCallSheets: (eventId) => api.post(`/events/${eventId}/call-sheets/generate`),
};

// Schedule API
export const scheduleAPI = {
  getGlobal: (params) => api.get('/schedule/global', { params }),
  getTrucking: (params) => api.get('/schedule/trucking', { params }),
  updateTrucking: (eventId, scheduleId, data) => api.put(`/schedule/trucking/${eventId}/${scheduleId}`, data),
};

// Trucking Week at a Glance API
export const truckingAPI = {
  weekAtAGlance: (weekOffset = 0) => api.get('/trucking/week-at-a-glance', { params: { week_offset: weekOffset } }),
};

// Standalone Trucking API
export const standaloneTruckingAPI = {
  list: (params) => api.get('/trucking/standalone', { params }),
  create: (data) => api.post('/trucking/standalone', data),
  update: (id, data) => api.put(`/trucking/standalone/${id}`, data),
  delete: (id) => api.delete(`/trucking/standalone/${id}`),
};

// Notes API
export const notesAPI = {
  list: (entityType, entityId) => api.get(`/notes/${entityType}/${entityId}`),
  create: (data) => api.post('/notes', data),
  delete: (id) => api.delete(`/notes/${id}`),
};

// Activities API
export const activitiesAPI = {
  list: (entityType, entityId) => api.get(`/activities/${entityType}/${entityId}`),
  recent: (limit = 20) => api.get('/activities/recent', { params: { limit } }),
};

// Dashboard API
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
  weekAtAGlance: (weekOffset = 0) => api.get('/dashboard/week-at-a-glance', { params: { week_offset: weekOffset } }),
  myEvents: () => api.get('/dashboard/my-events'),
  myTruckingRuns: () => api.get('/dashboard/my-trucking-runs'),
};

// Search API
export const searchAPI = {
  global: (q, limit = 10) => api.get('/search', { params: { q, limit } }),
};

// Bug Reports API
export const bugReportsAPI = {
  create: (data) => api.post('/bug-reports', data),
  list: (params) => api.get('/bug-reports', { params }),
  get: (id) => api.get(`/bug-reports/${id}`),
  update: (id, data) => api.put(`/bug-reports/${id}`, data),
  delete: (id) => api.delete(`/bug-reports/${id}`),
  getCount: () => api.get('/bug-reports/count'),
};

// Feature Requests API
export const featureRequestsAPI = {
  create: (data) => api.post('/feature-requests', data),
  list: (params) => api.get('/feature-requests', { params }),
  get: (id) => api.get(`/feature-requests/${id}`),
  update: (id, data) => api.put(`/feature-requests/${id}`, data),
  delete: (id) => api.delete(`/feature-requests/${id}`),
  vote: (id, vote) => api.post(`/feature-requests/${id}/vote`, null, { params: { vote } }),
};

export default api;
